import React, { useState, useEffect } from 'react';
import { Plus, Search, User, Mail, Phone, Lock } from 'lucide-react';
import Skeleton from '../common/Skeleton';
import { Agency } from '../../services/auditService';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import ApprovalDot, { WorkflowState } from '../common/ApprovalDot';
import { NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import { timelineLabel } from '../../services/auditPlanService';

// APM-5.1 — reports the GRC team should expect back from an agency each year,
// one per period of its audit-plan timeline.
const PERIODS_PER_YEAR: Record<string, number> = { MONTHLY: 12, QUARTERLY: 4, HALF_YEARLY: 2, ANNUAL: 1 };

interface AuditAgenciesProps {
    agencies: Agency[];
    memberCounts?: Record<number, number>;
    loading?: boolean;
    onAdd: () => void;
    onEdit: (agency: Agency) => void;
    /** BRD#754: only GRC Team; record never deleted (BRD#755). */
    onInactivate: (id: number) => void;
    /** BRD#756: GRC Team may reactivate an Inactive Agency. */
    onReactivate: (id: number) => void;
    onManageMembers?: (agency: Agency) => void;
    onConfigure?: (agency: Agency) => void;
    /** Opens the status-dot workflow modal (submit/approve/reject/reopen). */
    onWorkflow?: (id: number) => void;
    /** RBAC gate — only Super Admin / GRC Audit Head / GRC Staffing Team
     * (role) AND the ADT_AGENCIES activity grant may create. Backend enforces
     * in adt_routes.py:_AGENCY_CREATOR_ROLES; gating the entry-point button
     * avoids dead-end 403s for unauthorized users. */
    canCreate?: boolean;
    /** True while the permission grants are still loading — disables the
     * Create button (no tooltip) to avoid a flash of an enabled button. */
    permLoading?: boolean;
}


const AuditAgencies: React.FC<AuditAgenciesProps> = ({
    agencies,
    // memberCounts = {},
    loading = false,
    onAdd,
    // onInactivate,      // soft-delete now lives in AgencyModal footer toggle
    // onReactivate,      // soft-delete now lives in AgencyModal footer toggle
    // onManageMembers,
    onConfigure,
    onWorkflow,
    canCreate = true,
    permLoading = false,
}) => {
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => { setPage(1); }, [search, pageSize, statusFilter]);

    const filtered = [...agencies]
        .sort((a, b) => b.id - a.id)
        .filter(a => {
            const matchesSearch =
                a.audit_agency_name.toLowerCase().includes(search.toLowerCase()) ||
                (a.agency_code || '').toLowerCase().includes(search.toLowerCase());
            const matchesStatus =
                statusFilter === 'all' ||
                (statusFilter === 'active' && (a.is_active ?? 1) === 1) ||
                (statusFilter === 'inactive' && (a.is_active ?? 1) === 0);
            return matchesSearch && matchesStatus;
        });

    const totalRecords = filtered.length;
    const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    const activeCount   = agencies.filter(a => (a.is_active ?? 1) === 1).length;
    const inactiveCount = agencies.filter(a => (a.is_active ?? 1) === 0).length;

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                {/* Status — iOS-style on/off switch (on = active) */}
                <StatusSwitch
                    value={statusFilter === 'inactive' ? 'INACTIVE' : 'ACTIVE'}
                    onChange={next => setStatusFilter(next === 'INACTIVE' ? 'inactive' : 'active')}
                    noun="agencies"
                    testId="SEL_audit_agencies_status_filter"
                />
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search agencies…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_audit_agencies_search"
                    />
                    <Button
                        variant="fill"
                        icon={Plus}
                        onClick={onAdd}
                        disabled={permLoading || !canCreate}
                        title={!canCreate ? NOT_AUTHORISED_MSG : undefined}
                        data-testid="BTN_audit_agencies_create"
                    >Create</Button>
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--dot"></th>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Code</th>
                                        <th className="table-list-th">Agency Name</th>
                                        <th className="table-list-th">Description</th>
                                        <th className="table-list-th">Point of Contact</th>
                                        <th className="table-list-th">Agency Type</th>
                                        <th className="table-list-th">Plan Timeline</th>
                                        {/* <th className="table-list-th">Members</th> */}
                                        <th className="table-list-th">Is Global</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['dot', '3xs', 'xs', 'md', 'md', 'sm', '2xs', '2xs', '2xs']} />}
                                    {!loading && filtered.length === 0 && (
                                        <tr>
                                            <td colSpan={9} className="table-list-cell">
                                                <NoDataFound
                                                    title={search || statusFilter !== 'all' ? 'No Matching Agencies' : 'No Agencies Found'}
                                                    message={search || statusFilter !== 'all' ? 'No agencies match your search or filter.' : 'Click "Create" to add one.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && paginatedRows.map((agency, idx) => {
                                        const isActive = (agency.is_active ?? 1) === 1;
                                        return (
                                            <tr
                                                key={agency.id}
                                                className={`table-list-row ${!isActive ? 'opacity-60' : ''}`}
                                                onClick={() => onConfigure && onConfigure(agency)}
                                                data-testid={`BTN_audit_agencies_row_${agency.id}_open`}
                                            >
                                                <td
                                                    className="table-list-cell table-list-cell--dot"
                                                    onClick={(e) => { e.stopPropagation(); onWorkflow && onWorkflow(agency.id); }}
                                                    title="Workflow — view timeline & act"
                                                    data-testid={`BTN_audit_agencies_row_${agency.id}_workflow`}
                                                >
                                                    <ApprovalDot
                                                        state={(((agency as any).workflow_state) || 'draft') as WorkflowState}
                                                        onClick={() => onWorkflow && onWorkflow(agency.id)}
                                                        title="Workflow — view timeline & act"
                                                    />
                                                </td>
                                                <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                                <td className="table-list-cell">
                                                    {agency.code
                                                        ? <span className="cell-code">{agency.code}</span>
                                                        : <span className="text-muted text-xs">—</span>}
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className="table-list-cell-title cell-name-lock">
                                                        {(agency as any).workflow_state === 'approved' && (
                                                            <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                                        )}
                                                        {agency.audit_agency_name}
                                                    </span>
                                                </td>
                                                <td className="table-list-cell text-sm text-gray-500">
                                                    {agency.description || '—'}
                                                </td>
                                                <td className="table-list-cell">
                                                    <div className="flex items-center gap-1.5 font-medium text-gray-700">
                                                        <User size={12} className="text-gray-400 shrink-0" />
                                                        {agency.contact_head_name || '—'}
                                                    </div>
                                                    <div className="flex flex-col gap-0.5 mt-0.5">
                                                        {agency.email && (
                                                            <span className="flex items-center gap-1 text-xs text-gray-500">
                                                                <Mail size={10} className="shrink-0" />{agency.email}
                                                            </span>
                                                        )}
                                                        {agency.phone && (
                                                            <span className="flex items-center gap-1 text-xs text-gray-500">
                                                                <Phone size={10} className="shrink-0" />{agency.phone}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                {/* <td className="table-list-cell">
                                                    <button
                                                        onClick={e => { e.stopPropagation(); onManageMembers && onManageMembers(agency); }}
                                                        className="table-list-action table-list-action--edit"
                                                        title="Manage members"
                                                        disabled={!isActive}
                                                    >
                                                        <Users size={14} />
                                                        <span className="ml-1 text-xs">{memberCounts[agency.id] ?? 0}</span>
                                                    </button>
                                                </td> */}
                                                <td className="table-list-cell">
                                                    {agency.agency_kind === 'Internal'
                                                        ? <span className="status-badge sm success">Internal</span>
                                                        : <span className="status-badge sm info">External</span>
                                                    }
                                                </td>
                                                <td className="table-list-cell" data-testid={`LBL_agency_row_${agency.id}_timeline`}>
                                                    <span className="status-badge sm neutral">{timelineLabel(agency.plan_periodicity)}</span>
                                                    <div className="text-xs text-gray-700" style={{ marginTop: 4, whiteSpace: 'nowrap' }} title={agency.plan_year_type === 'CALENDAR' ? 'Calendar year' : 'Financial year'}>
                                                        {agency.plan_start_period_label ?? '—'} → {agency.plan_end_period_label ?? 'open-ended'}
                                                    </div>
                                                    <div className="text-xs text-gray-500" style={{ marginTop: 4, whiteSpace: 'nowrap' }}>
                                                        {(() => {
                                                            const n = PERIODS_PER_YEAR[agency.plan_periodicity ?? 'QUARTERLY'] ?? 4;
                                                            return `${n} report${n === 1 ? '' : 's'} / year`;
                                                        })()}
                                                    </div>
                                                </td>
                                                <td className="table-list-cell">
                                                    {agency.is_global === 1
                                                        ? <span className="status-badge sm info">Global</span>
                                                        : <span className="status-badge sm neutral">Entity</span>
                                                    }
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            recordLabel="agencies"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuditAgencies;
